/**
 * EndpointControl - Renders the appropriate control widget for an endpoint
 *
 * Used by both EndpointRow (in graph nodes) and NodeInspector (in side panel).
 */

import { ValueDisplay } from '../../shared/valueControls/ValueDisplay';
import { SliderControl } from '../../shared/valueControls/SliderControl';
import { SelectControl } from '../../shared/valueControls/SelectControl';
import { ArrayControl } from '../../shared/valueControls/ArrayControl';
import { BooleanControl } from '../../shared/valueControls/BooleanControl';
import { ColorControl } from '../../shared/valueControls/ColorControl';
import { StringControl } from '../../shared/valueControls/StringControl';
import { DefinitionSelectControl } from '../../shared/valueControls/DefinitionSelectControl';
import { getEndpointColor, extractAdoptedGet } from '../../shared/utils';
import { Endpoint } from '@nodes/Endpoint';
import { EndpointState } from '@/nodes/Endpoint';
import { useRuntime } from '@/ui/contexts/RuntimeProvider';
import { useCallback, useMemo } from 'react';
import { presetService } from '@/serverClient/presetService';
import { resolveConfigValue } from '@/runtime/util';
import type { RGBA } from '@plugins/threejs/colorUtils';


interface EndpointControlProps {

  /** Display value for sliders (local state during dragging) */
  displayValue?: number;
  instanceId: string;
  nodeId: string,
  endpoint: EndpointState;


  /** Show preset save button for select controls */
  showPresetSave?: boolean;
  onSavePreset?: () => void;
}

export function EndpointControl({
  instanceId,
  nodeId,
  endpoint
}: EndpointControlProps) {
  const color = getEndpointColor(endpoint.dataType, endpoint.dataFlow);
  const { getValue, tweakValue, fireTrigger, setEndpointMetadata, getEndpointValue, getPayload, markJukeboxDirty } = useRuntime();

  // Internal: only show if stringify returns non-empty
  if (endpoint.dataFlow === 'internal') {
    const str = endpoint.stringify(getValue(instanceId, nodeId, endpoint.id));
    return str ? <ValueDisplay value={str} /> : null;
  }

  // Only param and trigger dataFlows have controls (plus event when useExp is active)
  if (endpoint.dataFlow !== 'param' && endpoint.dataFlow !== 'trigger') {
    if (endpoint.dataFlow === 'event' && endpoint.useExp && endpoint.exp) {
      // Show the expression editor for event endpoints with useExp
      return (
        <StringControl
          value={endpoint.exp}
          onChange={(src) => setEndpointMetadata(instanceId, nodeId, endpoint.id, 'exp', src)}
        />
      );
    }
    return null;
  }

  // Trigger button for trigger endpoints
  if (endpoint.dataFlow === 'trigger') {
    const onTrigger = (e: React.MouseEvent) => {
      e.stopPropagation();
      e.preventDefault();
      fireTrigger(instanceId, nodeId, endpoint.id);
    };
    return (
      <div style={{ display: 'flex', justifyContent: 'flex-end', width: '100%' }}>
        <button
          className="nodrag nopan trigger-btn"
          onMouseDown={onTrigger}
          style={{
            background: 'transparent',
            border: 'none',
            color: '#9ca3af',
            fontSize: 12,
            padding: '2px 6px',
            cursor: 'pointer',
          }}
          title={`Fire ${endpoint.id} trigger`}
        >
          !
        </button>
      </div>
    );
  }

  const value = getValue(instanceId, nodeId, endpoint.id)

  const onChange = useCallback(val => {
    tweakValue(instanceId, nodeId, endpoint.id, val);
    if (endpoint.tweakable) markJukeboxDirty();
  }, [instanceId, nodeId, endpoint.id, endpoint.tweakable, tweakValue, markJukeboxDirty])

  const onSavePreset = useCallback(async () => {
    const presetName = prompt('Save preset as:', value ? `${value} (Custom)` : 'Custom');
    if (presetName) {
      const success = await presetService.savePresetFromNode(instanceId, nodeId, presetName);
      /*  if (success) {
          handleChange(presetName);
        }*/
    }
  }, [instanceId, nodeId, value]);

  // Detect adopted getter from store payload (upstream connection set get on this endpoint)
  const payload = getPayload(instanceId, nodeId, endpoint.id);
  const adoptedGet = useMemo(() => extractAdoptedGet(endpoint.useExp, payload?.get), [endpoint.useExp, payload?.get]);

  const { dataType, dataFlow, min, max, step, options, optionLabels, optionInfo } = endpoint;

  if (endpoint.id === 'preset') {
    return (<div style={{ display: 'flex', justifyContent: 'flex-end', alignItems: 'center', gap: 4, width: '50%' }}>
      <SelectControl
        value={value ?? options[0]}
        options={options as string[]}
        optionLabels={optionLabels?.length ? optionLabels : undefined}
        optionInfo={optionInfo?.length ? optionInfo : undefined}
        color={color}
        onChange={onChange}
      />

      <button
        onClick={onSavePreset}
        style={{
          background: '#059669',
          border: 'none',
          borderRadius: 3,
          color: 'white',
          fontSize: 10,
          padding: '2px 6px',
          cursor: 'pointer',
          whiteSpace: 'nowrap'
        }}
        title="Save current values as new preset"
      >
        Save
      </button>
    </div>)
  }


  // Build the control element
  let control: React.ReactNode;

  // Expression getter: show editable source text instead of slider
  if (endpoint.useExp && endpoint.exp) {
    control = (
      <StringControl
        value={endpoint.exp}
        onChange={(src) => setEndpointMetadata(instanceId, nodeId, endpoint.id, 'exp', src)}
      />
    );
  }
  // Adopted getter from upstream: show expression as read-only
  else if (adoptedGet) {
    control = <ValueDisplay value={`ƒ ${adoptedGet}`} />;
  }
  // Readonly param: display value with no editable control
  else if (endpoint.readonly) {
    control = <ValueDisplay value={value} />;
  }
  // Slider for numbers with min/max
  else if (dataType === 'number' && min !== undefined && max !== undefined && !options?.length) {
    control = (
      <SliderControl
        value={value}
        min={min}
        max={max}
        step={step}
        color={color}
        onChange={onChange}
      />
    );
  }
  // Custom control specified by endpoint metadata
  else if (endpoint.control === 'definitionSelect') {
    control = (
      <DefinitionSelectControl
        value={value}
        options={options as string[]}
        color={color}
        onChange={onChange}
      />
    );
  }
  // Color picker
  else if (dataType === 'color') {
    const endpointKey = `${nodeId}.${endpoint.id}`;
    const getAnimated = () => (resolveConfigValue(getEndpointValue(instanceId, endpointKey)) ?? { r: 0, g: 0, b: 0, a: 1 }) as RGBA;
    control = (
      <ColorControl
        value={value ?? { r: 0, g: 0, b: 0, a: 1 }}
        onChange={onChange}
        getAnimatedValue={getAnimated}
        options={options as (RGBA | string)[] | undefined}
        optionLabels={optionLabels as string[] | undefined}
      />
    );
  }
  // Select control when options array is present
  else if (options?.length) {
    control = (
      <div style={{ display: 'flex', justifyContent: 'flex-end', alignItems: 'center', gap: 4, width: '100%' }}>
        <SelectControl
          value={value ?? options[0]}
          options={options as string[]}
          optionLabels={optionLabels?.length ? optionLabels : undefined}
          optionInfo={optionInfo?.length ? optionInfo : undefined}
          color={color}
          onChange={onChange}
        />
      </div>
    );
  }
  // Array editor
  else if (dataType === 'array') {
    control = (
      <ArrayControl
        value={value}
        onChange={onChange}
      />
    );
  }
  // Boolean toggle
  else if (dataType === 'boolean') {
    control = (
      <div style={{ display: 'flex', justifyContent: 'flex-end', width: '100%' }}>
        <BooleanControl
          value={!!value}
          color={color}
          onChange={onChange}
        />
      </div>
    );
  }
  // String text input
  else if (dataType === 'string' && !endpoint.isOutput) {
    control = (
      <StringControl
        value={value ?? ''}
        onChange={onChange}
      />
    );
  }
  // Default: read-only display
  else {
    control = <ValueDisplay value={value} />;
  }

  return <>{control}</>;
}
